import { createRouter, createWebHistory } from "vue-router";
import { auth, analytics } from "@/firebase";

import Home from "@/components/pages/Home";
import About from "@/components/pages/About";
import RiverList from "@/components/pages/river/RiverList";
import RiverDetail from "@/components/pages/river/RiverDetail";
import NewUser from "@/components/pages/NewUser";
import Login from "@/components/pages/Login";
import ResetPassword from "@/components/pages/ResetPassword";
import MyProfile from "@/components/pages/MyProfile";
import StripePortal from "@/components/pages/subscription/StripePortal";
import InsertData from "@/components/pages/admin/InsertData";
import Dashboard from "@/components/pages/admin/Dashboard";
import AdminGaugeList from "@/components/pages/admin/GaugeList";
import AdminGaugeEdit from "@/components/pages/admin/GaugeEdit";
import AdminSections from "@/components/pages/admin/Sections";
import AdminUserList from "@/components/pages/admin/UserList";
import AdminUserEdit from "@/components/pages/admin/UserEdit";
import PartnerList from "@/components/pages/admin/PartnerList.vue";
import PartnerEdit from "@/components/pages/admin/PartnerEdit.vue";
import AdminDangerFlags from "@/components/pages/admin/DangerFlags.vue";
import AdminCameraList from "@/components/pages/admin/CameraList.vue";
import Partners from "@/components/pages/Partners.vue";
import InfoDebit from "@/components/pages/InfoDebit.vue";
import ActiveDangers from "@/components/pages/ActiveDangers.vue";

import { UserService } from "@/store/modules/user";
import { applySeoMeta, buildRouteSeo } from "@/utils/seo";

const routes = [
  {
    name: "accueil",
    path: "/",
    component: Home,
    meta: {
      title: "Info débit | Niveaux d'eau vive et rivières du Québec",
      description:
        "Québec Eau Vive (anciennement Quebec Whitewater) — la référence en info-débit pour les rivières sportives du Québec. Niveaux, tendances et recommandations de débit en temps réel pour planifier vos sorties.",
    },
  },
  {
    name: "info-debit",
    path: "/info-debit",
    component: InfoDebit,
    meta: {
      title: "Info débit | Info-débit des rivières du Québec",
      description:
        "Trouvez l'info débit en temps réel pour les rivières sportives du Québec. Info-débit, recommandations de niveaux et tendances pour planifier vos sorties en kayak et canot.",
    },
  },
  {
    name: "a-propos",
    path: "/a-propos",
    component: About,
    meta: {
      title: "Notre approche de l'info-débit | À propos — Québec Eau Vive",
      description:
        "Découvrez comment Québec Eau Vive calcule l'info-débit des rivières du Québec : sources de données, méthode d'extrapolation et recommandations de débit pour les pagayeurs.",
    },
  },
  {
    name: "partenaires",
    path: "/partenaires",
    component: Partners,
    meta: {
      title: "Partenaires",
      description:
        "Consultez les partenaires qui soutiennent Québec Eau Vive et la diffusion des informations sur les rivières sportives du Québec.",
    },
  },
  {
    name: "rivieres",
    path: "/rivieres",
    component: RiverList,
    meta: {
      title: "Liste des rivières",
      description:
        "Parcourez les rivières et sections d'eau vive du Québec par nom, région, classe et niveau pour planifier votre prochaine sortie.",
    },
  },
  {
    name: "dangers",
    path: "/dangers",
    component: ActiveDangers,
    meta: {
      title: "Dangers signalés sur les rivières",
      description:
        "Arbres, éboulis et rapides modifiés : les dangers actifs signalés par la communauté sur les rivières d'eau vive du Québec.",
    },
  },
  {
    name: "riviere",
    path: "/riviere/:slug",
    component: RiverDetail,
    meta: {
      requiresPremiumOrFreeAccess: true,
      title: "Conditions de rivière",
      description:
        "Consultez les informations de section, les tendances récentes et les recommandations de débit pour cette rivière du Québec.",
    },
  },
  {
    name: "nouvel-utilisateur",
    path: "/nouvel-utilisateur",
    component: NewUser,
    meta: {
      title: "Créer un compte",
      description:
        "Créez un compte Québec Eau Vive pour suivre vos sections favorites et accéder à votre espace membre.",
      robots: "noindex,nofollow",
    },
  },
  {
    name: "connexion",
    path: "/connexion",
    component: Login,
    meta: {
      title: "Connexion",
      description: "Connectez-vous à votre compte Québec Eau Vive.",
      robots: "noindex,nofollow",
    },
  },
  {
    name: "mon-profil",
    path: "/mon-profil",
    component: MyProfile,
    meta: {
      title: "Mon profil",
      description: "Gérez votre profil Québec Eau Vive et vos préférences de compte.",
      robots: "noindex,nofollow",
    },
  },
  {
    name: "reinitialiser-mot-de-passe",
    path: "/reinitialiser-mot-de-passe",
    component: ResetPassword,
    meta: {
      title: "Réinitialiser le mot de passe",
      description: "Réinitialisez le mot de passe de votre compte Québec Eau Vive.",
      robots: "noindex,nofollow",
    },
  },
  {
    name: "subscription",
    path: "/subscription",
    // Same page as the payment portal: the offer for non-members, the subscription for members
    component: StripePortal,
    meta: {
      title: "Abonnement",
      description: "Consultez les options d'abonnement de Québec Eau Vive.",
      robots: "noindex,nofollow",
    },
  },
  {
    name: "payment-portal",
    path: "/payment-portal",
    component: StripePortal,
    meta: {
      title: "Mon abonnement",
      description: "Gérez votre abonnement Québec Eau Vive.",
      robots: "noindex,nofollow",
    },
  },
  {
    name: "insert-data",
    path: "/admin/insert-data",
    component: InsertData,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-dashboard",
    path: "/admin/dashboard",
    component: Dashboard,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-gauge-list",
    path: "/admin/gauges-list",
    component: AdminGaugeList,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-gauge-edit",
    path: "/admin/gauges-edit/:slug",
    component: AdminGaugeEdit,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-gauge-add",
    path: "/admin/gauges-edit/new",
    component: AdminGaugeEdit,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-sections",
    path: "/admin/sections",
    component: AdminSections,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-user-list",
    path: "/admin/user-list",
    component: AdminUserList,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-user-edit",
    path: "/admin/user-edit/:slug",
    component: AdminUserEdit,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-user-add",
    path: "/admin/user-edit/new",
    component: AdminUserEdit,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-danger-flags",
    path: "/admin/dangers-signales",
    component: AdminDangerFlags,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-camera-list",
    path: "/admin/cameras",
    component: AdminCameraList,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-partner-list",
    path: "/admin/partner-list",
    component: PartnerList,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-partner-edit",
    path: "/admin/partner-edit/:slug",
    component: PartnerEdit,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  {
    name: "admin-partner-add",
    path: "/admin/partner-edit/new",
    component: PartnerEdit,
    meta: { requiresAdmin: true, robots: "noindex,nofollow" },
  },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return new Promise((resolve, _reject) => {
      setTimeout(() => {
        if (savedPosition) {
          resolve(savedPosition);
        } else {
          resolve({ left: 0, top: 0 });
        }
      }, 500);
    });
  },
});

function getCurrentUser() {
  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

router.beforeEach(async (to, from, next) => {
  try {
    if (!to.matched.length) {
      next({ name: "accueil" });
      return;
    }

    const user = await getCurrentUser();
    let userRoles = null;
    if (user) {
      userRoles = await UserService.actions.getUserRoles(user.uid);
    }

    const isProtected = to.matched.some((route) => route.meta.requiresAdmin);
    const isAdmin = userRoles ? userRoles.isAdmin : false;
    //console.log(`IsAdmin ${isAdmin} isProtected ${isProtected}`);
    if (!isAdmin && isProtected) {
      next("/");
    } else {
      next();
    }
  } catch (error) {
    console.error("Navigation guard error:", error);
    next({ name: "accueil" });
  }
});

// Optional: Add a global error handler for navigation failures
router.onError((error) => {
  console.error("Router error:", error);
  router.replace({ name: "accueil" }).catch(() => {});
});

router.afterEach((to) => {
  applySeoMeta(buildRouteSeo(to));

  if (analytics) {
    analytics.logEvent("page_view", {
      page_path: to.fullPath,
      page_title: to.name || document.title,
    });
  }
});

export default router;
